import { Box, Stack, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ValuationHubCoreModelDtoValuationReportConfigurationDto } from "@/api/generatedApi";
import { InfoIcon } from "@/assets";
import { EnhancedTooltip } from "@/components/enhancedTooltip";
import { FormControlTextField } from "@/components/formControls";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  mainContainer: {
    display: "flex",
    gap: "30px",
    flexDirection: "column",
  },
  title: {
    color: "#1A1A1A",
    fontWeight: 700,
    fontSize: "18px",
    lineHeight: "150%",
  },
  description: {
    color: "#333333",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "150%",
  },
  commentsTitle: {
    color: "#333333",
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "150%",
  },
  formInput: {
    backgroundColor: "white",
    width: "100%",
    marginTop: "15px !important",
  },
  textFieldInput: {
    "& .MuiInputBase-root:not(.MuiInputBase-multiline)": {
      height: theme.analysisLayout.inputHeight,
    },
  },
  notesContainer: {
    justifyContent: "space-between",
    width: "100%",
  },
  notes: {
    color: "#555555",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "150%",
  },
});

export const PersonalizationSection = (): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { t } = useTranslation("ValuationReportGenerationPanel");
  const { control, watch } = useFormContext<ValuationHubCoreModelDtoValuationReportConfigurationDto>();
  const commentsSize = watch("comments")?.length || 0;

  return (
    <Box sx={styles.mainContainer}>
      <Stack alignItems={"start"} direction="column" spacing="6px" sx={{ width: "100%" }}>
        <Typography sx={styles.title}>{t("reportPanel.configuration.personalization")}</Typography>
        <Typography sx={styles.description}>{t("reportPanel.configuration.personalizationDescription")}</Typography>
      </Stack>
      <Stack alignItems={"start"} direction="column" spacing="6px" sx={{ width: "100%" }}>
        <Stack alignItems={"start"} direction="row">
          <Typography sx={styles.commentsTitle}>{t("reportPanel.configuration.comments")}</Typography>
          <EnhancedTooltip content={t("reportPanel.configuration.commentsTooltip")} placement="top">
            <Box>
              <InfoIcon sx={{ ml: 1, fontSize: theme.analysisLayout.titleFontSize, color: "primary.main" }} />
            </Box>
          </EnhancedTooltip>
        </Stack>
        <Typography sx={styles.description}>{t("reportPanel.configuration.commentsDescription")}</Typography>
        <FormControlTextField
          name="comments"
          control={control}
          multiline
          rows={6}
          sx={styles.formInput}
          textFieldSx={styles.textFieldInput}
          inputProps={{ maxLength: 2000 }}
          data-cy="report-configuration-comments-description"
        />
        <Stack alignItems={"start"} direction="row" sx={styles.notesContainer}>
          <Typography sx={styles.notes}>{t("reportPanel.configuration.commentsNote")}</Typography>
          <Typography
            sx={styles.notes}
          >{`${commentsSize}/2000 ${t("reportPanel.configuration.commentsCharacters")}`}</Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
